import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../../assets/css/KnowledgeBase.css";
import "../../assets/css/Urls.css";
import { getProjectPid } from "../../utils/sessionProject";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const ITEMS_PER_PAGE = 20;

const PromptHistory = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef("");
  const searchDebounceRef = useRef(null);

  const fetchStats = useCallback(async (page, search) => {
    const token = localStorage.getItem("token");
    const projectPid = getProjectPid();
    if (!projectPid) { setLoading(false); return; }
    setLoading(true);
    try {
      let url = `${BASE_URL}/api/v1/chat/projects/${projectPid}/user-stats?page=${page}&limit=${ITEMS_PER_PAGE}`;
      if (search?.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) {
        setItems(data.response?.items || []);
        setPagination(data.response?.pagination || { total: 0, totalPages: 1 });
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats(currentPage, searchRef.current);
  }, [currentPage, fetchStats]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })
      .replace("am", "AM").replace("pm", "PM");
    return `${date}, ${time}`;
  };

  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;

  const getPaginationItems = (cur, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (cur <= 4) return [1, 2, 3, 4, 5, "e1", total];
    if (cur >= total - 3) return [1, "e0", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "e0", cur - 1, cur, cur + 1, "e1", total];
  };

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        <div className="ph-list-search mb-3">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input
            type="search"
            className="form-control"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => {
              const val = e.target.value;
              searchRef.current = val;
              setSearchTerm(val);
              clearTimeout(searchDebounceRef.current);
              searchDebounceRef.current = setTimeout(() => {
                setCurrentPage(1);
                fetchStats(1, val);
              }, 400);
            }}
          />
        </div>

        <section className="url-table-card">
          <div className="table-responsive">
            <table className="table url-table ph-history-table table-hover align-middle mb-0">
              <colgroup>
                <col style={{ width: 48 }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "16%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-center">#</th>
                  <th>User</th>
                  <th>Sessions</th>
                  <th>
                    <span className="d-flex align-items-center gap-1">
                      <Clock size={12} />
                      Last Active
                    </span>
                  </th>
                  <th>
                    <span className="d-flex align-items-center gap-1">
                      <Zap size={12} />
                      Token Used
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center text-muted py-5">Loading...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="5" className="text-center text-muted py-5">No records found.</td></tr>
                ) : items.map((item, idx) => (
                  <tr
                    key={item.user_pid}
                    className="history-row"
                    onClick={() => navigate("/prompt-history-details", { state: { user: item } })}
                  >
                    <td className="text-center ph-idx-cell">{pageStart + idx + 1}</td>
                    <td className="ph-user-name">
                      {item.first_name} {item.last_name}
                    </td>
                    <td>
                      <span className="ph-session-badge">{item.session_count}</span>
                    </td>
                    <td className="ph-date-cell">{formatDate(item.last_active_at)}</td>
                    <td>
                      <span className="ph-token-badge">
                        <Zap size={10} />
                        {item.token_used?.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div className="url-pagination-wrap">
              <p className="url-pagination-summary mb-0">
                Showing {pageStart + 1}–{Math.min(pageStart + ITEMS_PER_PAGE, pagination.total)} of {pagination.total}
              </p>
              <nav>
                <ul className="url-pagination mb-0">
                  <li>
                    <button className="url-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                      <ChevronLeft size={15} />
                    </button>
                  </li>
                  {getPaginationItems(currentPage, pagination.totalPages).map((item, i) => (
                    <li key={i}>
                      {typeof item === "string" ? (
                        <span className="url-page-ellipsis">…</span>
                      ) : (
                        <button
                          className={`url-page-btn ${item === currentPage ? "active" : ""}`}
                          onClick={() => setCurrentPage(item)}
                        >
                          {item}
                        </button>
                      )}
                    </li>
                  ))}
                  <li>
                    <button className="url-page-btn" disabled={currentPage === pagination.totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                      <ChevronRight size={15} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default PromptHistory;
